import { TextField } from 'Containers/App/types';
import { DataRevisionConfig } from 'Utils/apiTypes';
import { sendInsightsEvent, updateInsightsDefaults } from 'Utils/insights';

import {
  ALEX_MEDICARE_B2B_BASE_URL,
  DEFAULT_MEDICARE_DESTINATION,
  MY_ALEX_HEALTH_URL,
  MedicareDestination,
} from '../constants';

export const parseMedicareDestination = (value?: string | null): MedicareDestination => {
  const known = Object.values(MedicareDestination).find((destination) => destination === value);
  return known ?? DEFAULT_MEDICARE_DESTINATION;
};

export const medicareDestinationFromConfig = (config?: DataRevisionConfig | null): MedicareDestination =>
  parseMedicareDestination(config?.benefits_counselor_evergreen?.settings?.medicare_destination);

// My ALEX Health needs no Medicare product. ALEX Medicare Education needs one published.
// No call-outs never shows.
export const shouldShowMedicareCallouts = (
  destination: MedicareDestination,
  hasMedicareProduct: boolean,
): boolean => {
  switch (destination) {
    case MedicareDestination.NO_CALL_OUTS:
      return false;
    case MedicareDestination.MY_ALEX_HEALTH:
      return true;
    default:
      return hasMedicareProduct;
  }
};

export const getMedicareCalloutUrl = (destination: MedicareDestination, customerPath: string): string =>
  destination === MedicareDestination.MY_ALEX_HEALTH
    ? MY_ALEX_HEALTH_URL
    : `${ALEX_MEDICARE_B2B_BASE_URL}/${customerPath}`;

const getMedicareInsightsType = (destination: MedicareDestination): 'B2B' | 'B2C' =>
  destination === MedicareDestination.MY_ALEX_HEALTH ? 'B2C' : 'B2B';

// Every Medicare call-out reports a click the same way.
export const trackMedicareProductSelect = (destination: MedicareDestination, url: string) =>
  updateInsightsDefaults({ product: 'medicare', product_url: url }).then(() => {
    sendInsightsEvent(null, 'product_select', {
      product: 'medicare',
      medicare_type: getMedicareInsightsType(destination),
    });
  });

const MEDICARE_INFO_CARD_FIELD = 'profile_section_addtl.medicare_info_card';

// Returns the Contentful field for a call-out's wording. My ALEX Health uses its own field
// when it has content; otherwise the standard field.
export const getMedicareCopyField = (
  destination: MedicareDestination,
  retrieveContentfulData: (field: string) => unknown,
  leaf: 'profile_side_panel_header' | 'profile_side_panel_button_text' | 'profile_callout_learn_more',
): TextField => {
  const myAlexHealthField = `${MEDICARE_INFO_CARD_FIELD}.my_alex_health.${leaf}`;
  const hasMyAlexHealthCopy =
    destination === MedicareDestination.MY_ALEX_HEALTH && !!retrieveContentfulData(myAlexHealthField);
  return (hasMyAlexHealthCopy ? myAlexHealthField : `${MEDICARE_INFO_CARD_FIELD}.${leaf}`) as TextField;
};
